// What3Words API service for converting between w3w addresses and coordinates

import config from '@/config';

const W3W_API_KEY = config.services.w3wApiKey;
const W3W_API_BASE = config.services.w3wApiUrl;

export interface W3WLocation {
  country: string;
  square: {
    southwest: { lng: number; lat: number };
    northeast: { lng: number; lat: number };
  };
  nearestPlace: string;
  coordinates: { lng: number; lat: number };
  words: string;
  language: string;
  map: string;
}

export interface W3WAutoSuggestResult {
  country: string;
  nearestPlace: string;
  words: string;
  distanceToFocusKm?: number;
  rank: number;
  language: string;
}

class What3WordsService {
  private hasApiKey(): boolean {
    return W3W_API_KEY.length > 0;
  }

  // Convert a what3words address to coordinates
  async convertToCoordinates(words: string): Promise<W3WLocation | null> {
    if (!this.hasApiKey()) {
      console.warn('What3Words API key not configured');
      return null;
    }

    // Clean the input - remove any leading /// or spaces
    const cleanWords = words.replace(/^\/\/\//, '').trim();
    
    // Validate format
    if (!this.isValidWhat3Words(cleanWords)) {
      console.error('Invalid what3words format:', cleanWords);
      return null;
    }

    try {
      const response = await fetch(
        `${W3W_API_BASE}/convert-to-coordinates?words=${encodeURIComponent(cleanWords)}&key=${W3W_API_KEY}`,
        {
          signal: AbortSignal.timeout(config.timeouts.default)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('What3Words API error:', error);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to convert what3words:', error);
      return null;
    }
  }

  // Convert coordinates to a what3words address
  async convertFrom3wa(lat: number, lng: number): Promise<W3WLocation | null> {
    if (!this.hasApiKey()) {
      console.warn('What3Words API key not configured');
      return null;
    }

    try {
      const response = await fetch(
        `${W3W_API_BASE}/convert-to-3wa?coordinates=${lat},${lng}&key=${W3W_API_KEY}`,
        {
          signal: AbortSignal.timeout(config.timeouts.default)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        console.error('What3Words API error:', error);
        return null;
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to convert coordinates to what3words:', error);
      return null;
    }
  }

  // Auto-suggest what3words addresses
  async autoSuggest(
    input: string,
    focus?: { lat: number; lng: number },
    nResults: number = 3
  ): Promise<W3WAutoSuggestResult[]> {
    if (!this.hasApiKey()) {
      return [];
    }

    try {
      let url = `${W3W_API_BASE}/autosuggest?input=${encodeURIComponent(input)}&n-results=${nResults}&key=${W3W_API_KEY}`;
      
      if (focus) {
        url += `&focus=${focus.lat},${focus.lng}`;
      }

      const response = await fetch(url, {
        signal: AbortSignal.timeout(config.timeouts.default)
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('What3Words API error:', error);
        return [];
      }

      const data = await response.json();
      return data.suggestions || [];
    } catch (error) {
      console.error('Failed to get what3words suggestions:', error);
      return [];
    }
  }

  // Validate what3words format
  isValidWhat3Words(words: string): boolean {
    // Remove any leading slashes
    const cleanWords = words.replace(/^\/\/\//, '').trim();
    
    // Basic validation: 3 words separated by dots
    // More comprehensive validation would check against actual word lists
    const pattern = /^[a-z]+\.[a-z]+\.[a-z]+$/;
    return pattern.test(cleanWords);
  }

  // Format what3words for display (with ///)
  formatForDisplay(words: string): string {
    const cleanWords = words.replace(/^\/\/\//, '').trim();
    return `///${cleanWords}`;
  }
}

export default new What3WordsService();